import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import directus, {
  DIRECTUS_DEFAULT_RETRY_COUNT,
  DIRECTUS_DEFAULT_RETRY_DELAY,
  type CollectionNames,
  type SingletonNames,
} from '@/api/clients/directus-client'
import {
  createItem,
  deleteItem,
  readItem,
  readItems,
  readSingleton,
  updateItem,
} from '@directus/sdk'
import { handleDirectusError } from './directusErrorHandler'

/**
 * Fetches a list of items from directus.
 *
 * @param {Object} options
 * @param {string} options.collection The name of the directus collection
 * @param {Record<string, unknown>} options.query The directus query to use with this request
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns a list of items of the type defined in passed type <T> if successful else undefined if errored.
 */
export async function directusReadByQuery<T>({
  collection,
  query,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  collection: CollectionNames
  query: Record<string, unknown>
  retryCount?: number
  retryDelay?: number
}): Promise<T[] | undefined> {
  let retries = 0
  let res
  while (retries <= retryCount) {
    try {
      res = await directus.request(readItems(collection, query))
      break // break out of the loop if the request is successful
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusReadByQuery failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
        return
      }
    }
    retries++
  }
  return res as T[]
}

/**
 * Fetches a single item from directus.
 *
 * @param {Object} options
 * @param {string} options.collection The name of the directus collection
 * @param {string} options.id The id of the item
 * @param {Record<string, unknown>} options.query Optional - the directus query to use with this request
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns a single item of the type defined in passed type <T> if successful else undefined if errored.
 */
export async function directusReadOne<T>({
  collection,
  id,
  query,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  collection: CollectionNames
  id: string | number
  query?: Record<string, unknown>
  retryCount?: number
  retryDelay?: number
}): Promise<T | undefined> {
  let retries = 0
  let res
  while (retries <= retryCount) {
    try {
      res = await directus.request(readItem(collection, id, query))
      break // break out of the loop if the request is successful
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusReadOne failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
        return
      }
    }
    retries++
  }
  return res as T
}

/**
 * Fetches a singleton item from Directus.
 *
 * @param {Object} options
 * @param {string} options.singleton The name of the Directus singleton.
 * @param {Record<string, unknown>} options.query Optional - Directus query to use with this request.
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns A singleton item of the type defined in the passed type <T> if successful, else undefined if errored.
 */
export async function directusReadSingletonItem<T>({
  singleton,
  query,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  singleton: SingletonNames
  query?: Record<string, unknown>
  retryCount?: number
  retryDelay?: number
}): Promise<T | undefined> {
  let retries = 0
  let res
  while (retries <= retryCount) {
    try {
      res = await await directus.request(readSingleton(singleton, query))
      break // break out of the loop if the request is successful
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusReadSingletonItem failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
        return
      }
    }
    retries++
  }
  return res as T
}

/**
 * Creates a new item in a directus collection.
 *
 * @param {Object} options
 * @param {string} options.collection The name of the directus collection.
 * @param {Partial<T>} options.item The object to be created can be an empty object if intending directus to apply defaults to the item.
 * @param {Record<string, unknown>} options.query Optional - the directus query to use with this request (alters the returned item by query).
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns a single item of the type defined in passed type <T> if successful else undefined if errored.
 */
export async function directusCreateOne<T>({
  collection,
  item,
  query,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  collection: CollectionNames
  item: Partial<T>
  query?: Record<string, unknown>
  retryCount?: number
  retryDelay?: number
}): Promise<{ success: boolean; data: T | undefined }> {
  let retries = 0
  let res
  while (retries <= retryCount) {
    try {
      res = await directus.request(createItem(collection, item, query))
      break // break out of the loop if the request is successful
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusCreateOne failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
        return { success: false, data: undefined }
      }
    }
    retries++
  }
  return { success: true, data: res as T }
}

/**
 * Deletes a single item from a directus collection.
 *
 * @param {Object} options
 * @param {string} options.collection The name of the directus collection.
 * @param {string} options.id The id of the item to be deleted
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns "ok" if successfully deleted or "failed" if not
 */
export async function directusDeleteOne({
  collection,
  id,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  collection: CollectionNames
  id: string | number
  retryCount?: number
  retryDelay?: number
}): Promise<'ok' | 'failed'> {
  let retries = 0
  while (retries <= retryCount) {
    try {
      await directus.request(deleteItem(collection, id))
      return 'ok'
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusDeleteOne failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
      }
    }
    retries++
  }
  return 'failed'
}

/**
 * Updated a single item in a directus collection.
 *
 * @param {Object} options
 * @param {string} options.collection The name of the directus collection.
 * @param {string} options.id The id of the item to be updated.
 * @param {Partial<T>} options.item The partial item object of the item containing the fields that need to be updated.
 * @param {Record<string, unknown>} options.query Optional - the directus query to use with this request (alters the returned item by query).
 * @param {number} options.retryCount The number of times to retry the request if it fails.
 * @param {number} options.retryDelay The delay in milliseconds between retries.
 * @returns the updated item if successful, undefined if not.
 */
export async function directusUpdateOne<T>({
  collection,
  id,
  item,
  query,
  retryCount = DIRECTUS_DEFAULT_RETRY_COUNT,
  retryDelay = DIRECTUS_DEFAULT_RETRY_DELAY,
}: {
  collection: CollectionNames
  id: string | number
  item: Partial<T>
  query?: Record<string, unknown>
  retryCount?: number
  retryDelay?: number
}): Promise<T | undefined> {
  let retries = 0
  let res
  while (retries <= retryCount) {
    try {
      res = await directus.request(updateItem(collection, id, item, query))
      break // break out of the loop if the request is successful
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        if (import.meta.env.VITE_VERBOSE_MODE === 'true')
          console.error('directusUpdateOne failed.', error)
      } else notifyAnalytics({ type: 'log_error', data: error })
      if (retries < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
      } else {
        handleDirectusError(error)
        return
      }
    }
    retries++
  }
  return res as T
}

/**
 * Cleans a passed record/partial record item and removes the system generated properties directus will handle the updating of.
 *
 * @param data full/partial item.
 * @returns cleaned partial item.
 */
export function cleanOutSystemGeneratedProperties<T extends Directus_base_item>(
  data: Partial<T>,
): Partial<T> {
  // Ensure no system generate properties that should be only auto updated are included.
  const preparedData = {
    ...data,
  }

  const fieldsToDelete = [
    'id',
    'user_created',
    'date_created',
    'user_updated',
    'date_updated',
    'sort',
  ]

  fieldsToDelete.forEach((field) => {
    if (field in preparedData) {
      delete (preparedData as Record<string, unknown>)[field]
    }
  })

  return preparedData
}

interface Directus_base_item {
  id?: string | number
  user_created?: string | null
  date_created?: string | null
  user_updated?: string | null
  date_updated?: string | null
  sort?: number | null
}
